import type { SmileSimulationEntity } from "@repo/core/firestore/smileSimulation";

import type {
  SmileSimulationPatientSummaryViewModel,
  SmileSimulationPatientViewModel,
  SmileSimulationRecordViewModel,
} from "../model/view-model";

const normalizeChartNumber = (chartNumber: string) =>
  chartNumber.trim().toLowerCase();

export const createSmileSimulationPatientId = (chartNumber: string) =>
  `patient-${encodeURIComponent(normalizeChartNumber(chartNumber)).replace(/%/g, "_")}`;

const sortSmileSimulationRecordsDescending = (
  records: SmileSimulationRecordViewModel[],
) =>
  [...records].sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );

export const createSmileSimulationPatientViewModel = (
  patient: Pick<SmileSimulationPatientViewModel, "chartNumber" | "id" | "name">,
): SmileSimulationPatientViewModel => ({
  id: patient.id,
  name: patient.name,
  chartNumber: patient.chartNumber,
});

export const mapSmileSimulationEntityToRecordViewModel = (
  entity: SmileSimulationEntity,
  imageUrls: {
    captureImage: string;
    resultImage: string;
  },
): SmileSimulationRecordViewModel => {
  const chartNumber = entity.chartNumber ?? entity.id;
  const patientName = entity.patientName ?? chartNumber;

  return {
    id: entity.id,
    patientId: createSmileSimulationPatientId(chartNumber),
    patientName,
    chartNumber,
    captureImage: imageUrls.captureImage,
    resultImage: imageUrls.resultImage,
    createdAt: entity.createdAt.toDate().toISOString(),
    imageModel: entity.imageModel,
    provider: entity.provider,
    openAiQuality: entity.openAiQuality,
    promptVersion: entity.promptVersion,
    status: "completed",
  };
};

export const groupSmileSimulationRecordsByPatient = (
  records: SmileSimulationRecordViewModel[],
): SmileSimulationPatientSummaryViewModel[] => {
  const summaries = new Map<string, SmileSimulationPatientSummaryViewModel>();

  for (const record of sortSmileSimulationRecordsDescending(records)) {
    const existingSummary = summaries.get(record.patientId);

    if (!existingSummary) {
      summaries.set(record.patientId, {
        id: record.patientId,
        name: record.patientName,
        chartNumber: record.chartNumber,
        createdAt: record.createdAt,
        lastSimulatedAt: record.createdAt,
        latestRecord: record,
        recordCount: 1,
        records: [record],
      });
      continue;
    }

    existingSummary.name = record.patientName;
    existingSummary.chartNumber = record.chartNumber;
    existingSummary.recordCount += 1;
    existingSummary.records.push(record);

    if (
      new Date(record.createdAt).getTime() <
      new Date(existingSummary.createdAt).getTime()
    ) {
      existingSummary.createdAt = record.createdAt;
    }
  }

  return [...summaries.values()]
    .map((summary) => ({
      ...summary,
      records: sortSmileSimulationRecordsDescending(summary.records),
      latestRecord:
        sortSmileSimulationRecordsDescending(summary.records)[0] ?? null,
    }))
    .sort((left, right) => {
      const leftTime = new Date(
        left.latestRecord?.createdAt ?? left.lastSimulatedAt ?? left.createdAt,
      ).getTime();
      const rightTime = new Date(
        right.latestRecord?.createdAt ??
          right.lastSimulatedAt ??
          right.createdAt,
      ).getTime();

      return rightTime - leftTime;
    });
};
